import { useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { useTranslation } from 'react-i18next';
import { AdminContext } from '../contexts/admin.context';

import Sidebar from './Sidebar';
import Button from './common/Button';
import ConfirmationPopup from './common/ConfirmationPopup';
import ContentPopup from './common/ContentPopup';
import FullScreenLoader from './common/fullScreenLoader';
import LanguageSelectDropdown from './common/languageSelectDropdown';

import { CONTENT_POPUP_DEFAULT_VALUES, POPUP_DEFAULT_VALUES } from '../data/default-values';
import {
  getAccountDetails,
  getUserDetails,
  setAuthToken,
  setOrgHeaders,
  teacherApi
} from '../service';
import { getTranslation, setMod, setRegion } from '../utils/translation';
import { XIcon } from './icons';

const DashboardLayout = ({ basePath }) => {
  const navigate = useNavigate();

  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();

  const videoRef = useRef(null);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState({});
  const [openSidebar, setOpenSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const pageHasBackButton = pathname.includes('/edit-project');

  const {
    popupInfo,
    setPopupInfo,
    contentPopupInfo,
    setContentPopupInfo,
    themeId,
    teamType,
    setTRegion,
    user,
    setUser,
    pipVideo,
    setPipVideo,
    portalConfig: { adminPanelTabs, languages } = {},
    themeConfig: { secondary } = {}
  } = useContext(AdminContext);

  const handlePopupHide = () => {
    // TODO: update
    setPopupInfo(POPUP_DEFAULT_VALUES);
  };

  const handleContentPopupHide = () => {
    setContentPopupInfo(CONTENT_POPUP_DEFAULT_VALUES);
  };

  const handleChange = (lang) => {
    i18n.changeLanguage(lang?.value?.toLowerCase());
    try {
      localStorage?.setItem('lang', i18n.language);
    } catch (error) {
      console.log('LS unavailable');
    }
  };

  const handleLogout = () => {
    try {
      localStorage?.removeItem('token');
    } catch (error) {
      console.log('error', error);
    }
    navigate(basePath + '/login');
  };

  const togglePictureInPicture = async () => {
    if (document.pictureInPictureElement) {
      // If PiP is active, exit it
      await document.exitPictureInPicture();
    } else if (videoRef.current) {
      // If PiP is not active, request it for the video element
      await videoRef.current.requestPictureInPicture();
    }
  };

  // Update translation prefix if applies
  useEffect(() => {
    if (!user) return;

    try {
      setMod(!user?.administrator && user?.moderator ? 'mod' : '');

      const userRegion = user?.address?.[0]?.street;

      const regionTranslationExisting = Object.keys(adminPanelTabs)?.find(
        (key) => key === userRegion
      );
      if (!regionTranslationExisting) {
        setTRegion('');
        setRegion('');
        return;
      }

      setTRegion(userRegion);
      setRegion(userRegion);
    } catch (error) {
      console.log('error', error);
    }
  }, [user]);

  // Get logged user data
  useEffect(() => {
    const options = languages
      ?.filter((lang) => lang !== 'en')
      ?.map((lang) => ({
        label: lang?.toUpperCase(),
        value: lang
      }));
    setLanguageOptions(options);

    let lastLanguage = languages?.[0];

    try {
      lastLanguage = localStorage.getItem('lang')?.toLowerCase();
    } catch (error) {
      console.log('LS unavailable');
    }

    try {
      try {
        const token = localStorage.getItem('token');
        setAuthToken(token);
      } catch (error) {
        console.log('LS unavailable');
      }

      const token = teacherApi.defaults.headers.common['Authorization'];
      if (!token || token === 'Bearer null') throw new Error('No token found');

      setOrgHeaders(themeId, teamType);

      (async () => {
        try {
          const response = await getUserDetails();
          const accountDetailsResponse = await getAccountDetails();
          const finalUserObject = {
            ...response.data,
            administrator: response.data?.roles?.some(
              (role) => role?.id === 'administrator' || role?.id === 'user'
            ),
            moderator: response.data?.roles?.some((role) => role?.id === 'moderator'),
            active: response.data?.active,
            verified: response.data?.verified,
            address: accountDetailsResponse.data?.addresses,
            phone: accountDetailsResponse.data?.phone
          };
          setUser(finalUserObject);

          const setLanguage = languages?.includes(lastLanguage)
            ? lastLanguage
            : languages.includes(finalUserObject?.languageId)
            ? finalUserObject?.languageId
            : languages?.[0] || 'fr';

          i18n.changeLanguage(setLanguage);
        } catch (error) {
          console.log('error', error);
          navigate(basePath + '/login');
        } finally {
          setIsLoading(false);
        }
      })();
    } catch (e) {
      console.log('error', e);
      navigate(basePath + '/login');
    }
  }, []);

  useEffect(() => {
    setSelectedLanguage({
      label: i18n.language.toUpperCase(),
      value: i18n.language
    });
  }, [i18n.language]);

  useEffect(() => {
    if (window.innerWidth < 640) {
      setOpenSidebar(false);
    } else {
      setOpenSidebar(true);
    }
  }, [window.innerWidth]);

  if (isLoading) return <FullScreenLoader />;

  return (
    <>
      {/* POPUP */}
      {popupInfo.isVisible && (
        <ConfirmationPopup
          useLeftArrow={popupInfo.useLeftArrow}
          heading={popupInfo.heading}
          icon={popupInfo.icon}
          iconBg={popupInfo.iconBg}
          description={popupInfo.description}
          action={popupInfo.action}
          cancelButton={popupInfo.cancelButton}
          textBox={popupInfo.textBox}
          footerNotes={popupInfo.footerNotes}
          actionList={popupInfo.actionList}
          actionListClick={popupInfo.actionListClick}
          onHide={handlePopupHide}
          zIndex={popupInfo.zIndex}
        />
      )}
      {contentPopupInfo.isVisible && (
        <ContentPopup
          useLeftArrow={contentPopupInfo.useLeftArrow}
          title={contentPopupInfo.title}
          content={contentPopupInfo.content}
          maxWidth={contentPopupInfo.maxWidth}
          onHide={handleContentPopupHide}
        />
      )}

      <div className="min-h-screen w-full flex flex-col overflow-hidden bg-white pt-10">
        {/* BAR */}
        {/* <div className="w-full lg:block">
              <div className="bg-primary h-8 w-full"></div>
            </div> */}
        {/* Language Select option */}
        <div className="absolute right-10 top-3 z-10 flex flex-row items-center justify-center gap-5">
          {languageOptions?.length > 1 && (
            <LanguageSelectDropdown
              themeId={themeId}
              value={selectedLanguage}
              options={languageOptions}
              onChange={handleChange}
              className={'!mb-0'}
              styleType="mini"
            />
          )}

          <Button buttonType="primary" onClick={handleLogout}>
            {getTranslation(t, 'sideBar.logoutButton')}
          </Button>
        </div>
        {/* MAIN LAYOUT */}
        <div className="flex flex-grow">
          {openSidebar && <Sidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />}
          <div
            className={clsx(
              'w-full',
              { 'md:pl-[350px]': openSidebar },
              {
                'bg-white': pageHasBackButton
              }
            )}>
            <div className="relative px-8 lg:px-16 pt-10 w-full">
              <button
                className="mobile-menu-open mt-2 mb-6 p-2 rounded-md"
                style={{ backgroundColor: secondary, display: openSidebar ? 'none' : '' }}
                onClick={() => setOpenSidebar(!openSidebar)}>
                <svg
                  width="22"
                  height="18"
                  viewBox="0 0 22 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1 1H21M1 9H21M1 17H21"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <Outlet />
            </div>
          </div>
        </div>
      </div>

      {pipVideo && (
        <div className="fixed bottom-4 right-4 z-[9999] cursor-pointer outline-none">
          <div className="w-full flex justify-end mb-2">
            <button className="p-1" onClick={() => setPipVideo(null)}>
              <XIcon className="w-4 h-4" color="black" />
            </button>
          </div>
          <video
            ref={videoRef}
            src={pipVideo}
            controls
            autoPlay
            className="max-w-md max-h-80 mx-auto rounded-lg"
            onClick={togglePictureInPicture}
          />
        </div>
      )}
    </>
  );
};

export default DashboardLayout;
